//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Close, WarningAlt } from '@carbon/icons-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import TenantDataService from '../../utils/TenantDataService';

interface DeleteTokenModalProps {
  isOpen: boolean;
  tenantDataService: TenantDataService;
  onClose: () => void;
  /** Called after a successful delete, so callers can refresh their state. */
  onTokenChanged?: () => void;
}

type ModalMode = 'confirm' | 'done' | 'error';

const DeleteTokenModal: React.FC<DeleteTokenModalProps> = ({
  isOpen,
  tenantDataService,
  onClose,
  onTokenChanged,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ModalMode>('confirm');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setMode('confirm');
    setError(null);
    setBusy(false);
  }, [isOpen]);

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await tenantDataService.revokeMyToken();
      setMode('done');
      onTokenChanged?.();
    } catch (err: any) {
      setError(err.message || t('token.errors.deleteFailed'));
      setMode('error');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="delete-token-modal"
        className="max-w-lg p-0 dark:bg-[#262626]"
      >
        {/* Header */}
        <DialogTitle className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('token.delete.title')}
          </span>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('token.close')}
            className="rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid="delete-token-modal-close"
          >
            <Close size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </DialogTitle>

        {/* Content */}
        <div className="p-6">
          {mode === 'error' && (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          {mode === 'done' && (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('token.delete.success')}
            </p>
          )}

          {mode === 'confirm' && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <WarningAlt
                size={16}
                className="mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-400"
              />
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                {t('token.delete.confirm')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={busy}
            className="px-6"
            data-testid="delete-token-modal-cancel"
          >
            {mode === 'done' ? t('token.close') : t('token.cancel')}
          </Button>

          {mode === 'confirm' && (
            <Button
              onClick={handleDelete}
              disabled={busy}
              className="bg-red-600 px-6 text-white hover:bg-red-700"
              data-testid="delete-token-modal-confirm"
            >
              {busy ? t('token.delete.deleting') : t('token.delete.action')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteTokenModal;
